import { useState, useEffect } from 'react';
import moment from 'moment';

import { Alert, Button, Modal, message, Divider, Typography, Upload, Tag } from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';
import { Color } from 'utils/enums';
import { getDisplayCompanyId } from 'utils/helpers';
import { ORGANIZATION_NAME } from 'utils/objects';

import { GetSubmittedFileNames } from 'store/authorization/files/actions';
import { ModifyModal } from 'store/authorization/extendAndModify/actions';

import TableResult from 'components/Auth/TableResult';

import './index.scss';

const { Title } = Typography;
const { Dragger } = Upload;

interface AttachmentFormModalProps {
  attachmentFormModal: any,
  isModalVisibleHandler(): any,
  uploadFilestoAuth(options: any): any,
  getSubmittedFileNames(model: GetSubmittedFileNames): any,
  modifyAuth(model: ModifyModal): any,
  isModalVisible: boolean,
  isUploadingFilesToAuth: boolean,
  showHospitalDates: boolean,
};

const diagColumns = [
  {
    title: 'REFERENCE',
    key: 'ref',
    dataIndex: 'ref',
  },
  {
    title: 'DIAG CODE',
    key: 'diagCode',
    dataIndex: 'diagCode',
  },
  {
    title: 'DESCRIPTION',
    key: 'desc',
    dataIndex: 'desc',
  },
];

const cptColumns = [
  {
    title: 'CPT CODE',
    key: 'cpt',
    dataIndex: 'cpt',
  },
  {
    title: 'DESCRIPTION',
    key: 'desc',
    dataIndex: 'desc',
  },
  {
    title: 'MODIFIER',
    key: 'mod',
    dataIndex: 'mod',
  },
  {
    title: 'DIAG REF',
    key: 'diagRef',
    dataIndex: 'diagRef',
  },
  {
    title: 'QUANTITY',
    key: 'quantity',
    dataIndex: 'quantity',
  },
];

const notesColumns = [
  {
    title: 'SUBJECT',
    key: 'subject',
    dataIndex: 'subject',
  },
  {
    title: 'NOTES',
    key: 'notes',
    dataIndex: 'notes',
  },
];

const AttachmentFormModal = (props: AttachmentFormModalProps) => {

  const [fileAttachments, setFileAttachments] = useState<any[]>([]);
  const [symptomsVisible, setSymptomsVisible] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showHospitalDates, setShowHospitalDates] = useState(false);

  const handleRemove = (fileId: string) => {
    setFileAttachments(fileAttachments.filter((item: any) => item.uid !== fileId));
  };

  const handleOk = () => {

    const createUploadFileData = new FormData();
    createUploadFileData.append("companyId", props.attachmentFormModal.companyId);
    createUploadFileData.append("authNo", props.attachmentFormModal.authNo);
    createUploadFileData.append("memberId", props.attachmentFormModal.memberId);
    createUploadFileData.append("membLast", props.attachmentFormModal.memberLastName);
    createUploadFileData.append("membFirst", props.attachmentFormModal.memberFirstName);
    createUploadFileData.append("dob", moment(props.attachmentFormModal.dateOfBirth).format('YYYY-MM-DD'));
    createUploadFileData.append("authPcpId", props.attachmentFormModal.authPcpId);
    createUploadFileData.append("authPcpLast", props.attachmentFormModal.authPcpLastName);
    createUploadFileData.append("authPcpFirst", props.attachmentFormModal.authPcpFirstName);
    createUploadFileData.append("reqProvId", props.attachmentFormModal.requestProv);
    createUploadFileData.append("reqProvLast", props.attachmentFormModal.requestProviderLastName);
    createUploadFileData.append("reqProvFirst", props.attachmentFormModal.requestProviderFirstName);
    createUploadFileData.append("date", moment().toString());
    fileAttachments.forEach(fileAttachment => {
      createUploadFileData.append("authFile[]", fileAttachment);
    })

    props.uploadFilestoAuth(createUploadFileData)
      .then((_: any) => {
        const getSubmittedFileNamesData = {
          "AuthNo": props.attachmentFormModal.authNo,
          "CompanyId": props.attachmentFormModal.companyId,
          "SubmittedFileNames": fileAttachments.map(file => file.name)
        };

        props.getSubmittedFileNames(getSubmittedFileNamesData)
          .then((data: any[]) => {

            const listOfSubmittedFileNames: Array<string> = data.map(x => x.StoredFileName);
            const listOfOriginalFileNames: Array<string> = data.map(x => x.OriginalFileName);

            const modifyAuthFormData = {
              "RmiAction": "Authorization Attachment",
              "RmiAttachments": listOfOriginalFileNames,
              "RmiAuthNo": props.attachmentFormModal.authNo,
              "RmiCompanyId": props.attachmentFormModal.companyId,
              "RmiNote": null, //Legacy code has this as null for Authorization Attachment
              "RmiPriorityStatus": props.attachmentFormModal.priorityStatus,
              "RmiStatus": props.attachmentFormModal.statusNumber,
              "RmiStatusDescr": null, //Legacy code has this as null for Authorization Attachment
              "RmiSubmittedFileNames": listOfSubmittedFileNames,
              "RmiUpdateToPriorityStatus": props.attachmentFormModal.priorityStatus,
              "RmiUser": sessionStorage.user_id
            }

            props.modifyAuth(modifyAuthFormData)
              .then((response: number) => {
                if (response === 1) {
                  notifier.success('Attachment(s) has been submitted successfully.');
                  setShowCloseButton(true);
                }
              })
              .catch((err: ErrorResponse) => {
                notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
              });
          }).catch((err: ErrorResponse) => {
            notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
          });
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const handleCancel = () => {
    setShowCloseButton(false);
    setFileAttachments([]);
    props.isModalVisibleHandler();
  };

  // customRequest to just return a success to attach file to the form instead being actually uploading yet
  const customRequest = ({ onSuccess }: any) => {
    setTimeout(() => onSuccess('Success'), 0);
  };

  const beforeUpload = (file: any) => {

    const ext = file.name.substr(file.name.lastIndexOf('.') + 1);
    const allowFiles = ['tif', 'tiff', 'jpg', 'jpeg', 'pdf', 'doc', 'docx','TIF','TIFF','JPG','JPEG','PDF','DOC','DOCX'].includes(ext);
    if (!allowFiles) {
      message.error('You can only upload tif, tiff, jpg, jpeg, pdf, doc, docx, TIF, TIFF, JPG, JPEG, PDF, DOC, or DOCX file!');
      return false;
    } else {
      //file attachment used to list out the files under the dragger and cannot have duplicate files attached
      if (fileAttachments.filter(attachment => attachment.name === file.name).length === 0) {
        setFileAttachments((previous: any) => ([...previous, file]));
      } else {
        notifier.error('Please attach a file with a different name.');
      }
      return false;
    };
  }

  return (
    <Modal
      visible={props.isModalVisible}
      width={1000}
      onCancel={handleCancel}
      maskClosable={false}
      footer={
        showCloseButton ?
          [
            <Button
              key="close"
              type="primary"
              onClick={handleCancel}
            >
              Close
            </Button>
          ] :
          [
            <Button
              key="cancel"
              onClick={handleCancel}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              form="attachmentForm"
              onClick={handleOk}
              loading={props.isUploadingFilesToAuth}
            >
              Submit
            </Button>
          ]
      }
    >
      <div className="row flex-lg-wrap justify-content-center">
        <Title level={4}>Authorization Attachment</Title>
        <div className="attachment-form-modal row flex-lg-wrap justify-content-start mt-3">
          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Auth Number:</th>
                  <td>
                    {getDisplayCompanyId(props.attachmentFormModal.companyId)} - {props.attachmentFormModal.authNo}
                  </td>
                </tr>
                                         
                {props.showHospitalDates ? (
                  <>                    
                    <tr>
                      <th>Admit Date:</th>
                      <td>
                        {props.attachmentFormModal.rmiAdmitDate ? moment(props.attachmentFormModal.rmiAdmitDate).format('MM/DD/YYYY') :  ''}
                      </td>
                    </tr>
                  </>
                ) : 
                (<>
                  <tr>
                  <th>Request Date:</th>
                  <td>
                    {props.attachmentFormModal.requestDate ? moment(props.attachmentFormModal.requestDate).format('MM/DD/YYYY') : null}
                  </td>
                  </tr>
                  <tr>
                  <th>Referral Type:</th>
                  <td>
                    {props.attachmentFormModal.referenceType}
                  </td>
                  </tr>
                </>
                )}
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Status:</th>
                  <td>
                    { props.attachmentFormModal.status === 'APPROVED' && <Tag color={Color.APPROVED}>APPROVED</Tag> }
                    { props.attachmentFormModal.status === 'MODIFIED' && <Tag color={Color.MODIFIED}>MODIFIED</Tag> }
                    { props.attachmentFormModal.status === 'DEFERRED' && <Tag color={Color.DEFERRED}>DEFERRED</Tag> }
                    { props.attachmentFormModal.status === 'CANCELLED' && <Tag color={Color.CANCELLED}>CANCELLED</Tag> }
                    { props.attachmentFormModal.status === 'EXPIRED' && <Tag color={Color.EXPIRED}>EXPIRED</Tag> }
                    { props.attachmentFormModal.status === 'DENIED' && <Tag color={Color.DENIED}>DENIED</Tag>}
                    { props.attachmentFormModal.status === 'PENDED' && <Tag color={Color.PENDED}>PENDED</Tag>}
                    { props.attachmentFormModal.status === 'REQUESTED' && <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> }
                    { props.attachmentFormModal.status === 'SYSTEM_HOLD' && <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> }
                  </td>
                </tr>
                {props.showHospitalDates ? (
                  <>                    
                    <tr>
                      <th>Discharge Date:</th>
                      <td>{props.attachmentFormModal.rmiDschDate ? moment(props.attachmentFormModal.rmiDschDate).format('MM/DD/YYYY') :  ''}</td>
                    </tr>
                  </>
                  ) : (
                  <>
                  <tr>
                    <th>Expiration Date:</th>
                    <td>
                      {props.attachmentFormModal.expirationDate ? moment(props.attachmentFormModal.expirationDate).format('MM/DD/YYYY') : null}
                    </td>
                  </tr>
                  <tr>
                    <th>Retro Date:</th>
                    <td>
                    {props.attachmentFormModal.retroDate ? moment(props.attachmentFormModal.retroDate).format('MM/DD/YYYY'): null}
                    </td>
                  </tr>
                  </>
                  )
                }
                
              </tbody>
            </table>
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Patient Name:</th>
                  <td>
                    {props.attachmentFormModal.memberName}
                  </td>
                </tr>
                <tr>
                  <th>Address:</th>
                  <td>
                    {props.attachmentFormModal.memberAddress}
                  </td>
                </tr>
                <tr>
                  <th>City, State, Zip:</th>
                  <td>
                    {props.attachmentFormModal.memberCity}, {props.attachmentFormModal.memberState} {props.attachmentFormModal.memberZipCode}
                  </td>
                </tr>
                <tr>
                  <th>Health Plan:</th>
                  <td>
                    {props.attachmentFormModal.healthPlan}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Date of Birth:</th>
                  <td>
                    {props.attachmentFormModal.dateOfBirth ? moment(props.attachmentFormModal.dateOfBirth).format('MM/DD/YYYY') : null}
                  </td>
                </tr>
                <tr>
                  <th>Gender:</th>
                  <td>
                    {props.attachmentFormModal.gender}
                  </td>
                </tr>
                <tr>
                  <th>Member ID:</th>
                  <td>
                    {props.attachmentFormModal.memberId}
                  </td>
                </tr>
                <tr>
                  <th>Member PCP:</th>
                  <td>
                    {props.attachmentFormModal.memberPcp}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Request Provider:</th>
                  <td>
                    {props.attachmentFormModal.referralBy}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Referral To:</th>
                  <td>
                    {props.attachmentFormModal.referralTo}
                  </td>
                </tr>
                <tr>
                  <th>Specialty:</th>
                  <td>
                    {props.attachmentFormModal.referralSpecialty}
                  </td>
                </tr>
                <tr>
                  <th>POS:</th>
                  <td>
                    {props.attachmentFormModal.referralPos}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <th>Address:</th>
                  <td>
                    {props.attachmentFormModal.referralToAddress} <br />
                    {props.attachmentFormModal.referralToCity}, &nbsp; {props.attachmentFormModal.referralToState} &nbsp; {props.attachmentFormModal.referralToZipCode}
                  </td>
                </tr>
                <tr>
                  <th>Phone Number:</th>
                  <td>
                    {props.attachmentFormModal.referralToPhoneNumber}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <div className="col-lg-24">
            <TableResult columns={diagColumns} dataSource={props.attachmentFormModal.authDiagsData} />
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <div className="col-lg-24">
            <TableResult columns={cptColumns} dataSource={props.attachmentFormModal.authDetailsData} />
          </div>

          <Divider className="attachment-form-modal-divider"/>

          <Divider />

          <div className="col-lg-24 col-md-24">
            <div className="row">
              <div className="col-lg-7">
                Symptoms and Treatments:
                { props.attachmentFormModal.showNotes &&
                  <TableResult columns={notesColumns} dataSource={props.attachmentFormModal.authNotesData} />
                }
              </div>
              <div className="col-lg-5">
                <Button
                  type="primary"
                  onClick={() => setSymptomsVisible(!symptomsVisible)}
                  style={{ width: '100%' }}
                >
                  {symptomsVisible ? 'Hide' : 'Show'}
                </Button>
              </div>
              <div className="col-lg ml-2">
                {symptomsVisible && (
                  <>
                    {props.attachmentFormModal?.authNotesData?.[0]?.notes && (
                      <>
                        {props.attachmentFormModal.authNotesData[0].notes.split(/\r?\n/)[0]}
                        <br />
                        {`Submitted by ${props.attachmentFormModal.authNotesData[0].notes.split(/\r?\n/)[1]} on ${props.attachmentFormModal.authNotesData[0].notes.split(/\r?\n/)[2]}`}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <Divider />

          <div className="col-lg-24">
            Attachment:
            <ul>
            { props.attachmentFormModal.attachments.map((data: any, key: number) => {
                return <li key={key}> {data.fileMeta}</li>;
              })
            }
            </ul>
          </div>

          <div className="col-lg-24">
            <div>
              <Dragger
                fileList={fileAttachments}
                customRequest={customRequest}
                beforeUpload={beforeUpload}
                multiple={true}
                showUploadList={false}
                accept={'.tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx, .TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX'}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                  (Only files of type .tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx, .TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX will be accepted)
                </p>
              </Dragger>
            </div>

            <div className="col-lg-24 mt-3 mb-3">
              {fileAttachments.map((file: any, key: number) => (
                <div key={key}>
                  {file.name}{" "}
                  <span><i onClick={() => handleRemove(file.uid)} className="fa fa-trash" aria-hidden="true"></i></span>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-24 mb-4">
            <Alert
              message="Provider&#8217;s Responsibilities"
              description={
                <>
                  Eligibility must be verified 24 hours before the actual date of service.
                  Provider MUST confirm with Health Plan/IPA that the member&#8217;s health plan coverage is still in effect. {getDisplayCompanyId(props.attachmentFormModal.companyId)} reserves
                  the right to revoke this authorization prior to services being rendered based on cancellation of the member&#8217;s eligibility.
                  Final determination of benefits will be made after review of the claim for limitations or exclusions.
                </>
              }
              type="info"
              showIcon
            />
          </div>

          <div className="col-lg-12">
            {moment().format("MMM D, YYYY [at] h:mmA")}
          </div>

          <div className="col-lg-24 row flex-lg-wrap justify-content-left mb-1 ml-1">
            © {ORGANIZATION_NAME}, Inc. {moment().year()}, All Rights Reserved
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AttachmentFormModal;